// i18n
import translations from './i18n.json';
import { translateInComponent } from 'infras/i18n';

import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import { background, gray01, gray02 } from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import Header from 'presentations/views/components/organisms/Header';
import Headline from 'presentations/views/components/atoms/Headline';
import AppDownloadLink from 'presentations/views/components/molecules/AppDownloadLink';
import BackgroundContainer from 'presentations/views/components/organisms/service/BackgroundContainer';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import { useRouter } from 'next/router';
import {
  iosAppStorePath,
  macAppStorePath,
  playStorePath,
} from 'presentations/routes/path';

interface Props {
  viewer: ViewerItem | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  return (
    <ServicePageTemplate
      header={<Header viewer={props.viewer} />}
      breadcrumbs={[]}
    >
      <Page>
        <SectionContainerWrapper>
          <SectionContainer>
            <TitleContainer>
              <Title variant="h1">
                <span>{t('page_title')}</span>
              </Title>
              <Description>{t('page_description')}</Description>
            </TitleContainer>
            <SectionTitle variant="h3">{t('Mobile app')}</SectionTitle>
            <SectionText>{t('description mobile')}</SectionText>
            <DownloadContainer>
              <IOSAppDownloadLink
                link={iosAppStorePath(router.locale)}
                platform="apple"
                title={t('Download iOS App')}
              />
              <AppDownloadLink
                link={playStorePath()}
                platform="android"
                title={t('Download Android App')}
              />
            </DownloadContainer>
            <Container>
              <MainVisualIos
                src="pages/downloads/mv1.png"
                dpr={2}
                alt="iOS"
                loading="auto"
                width={340}
                height={330}
              />
              <MainVisualAndroid
                src="pages/downloads/mv2.png"
                dpr={2}
                alt="Android"
                loading="auto"
                width={160}
                height={332}
              />
            </Container>
          </SectionContainer>
          <SectionContainer>
            <SectionTitle variant="h3">{t('Desktop app')}</SectionTitle>
            <SectionText>{t('description desktop')}</SectionText>
            <DownloadContainer>
              <AppDownloadLink
                link={macAppStorePath(router.locale)}
                platform="apple"
                title={t('Download Mac App')}
              />
            </DownloadContainer>
            <Container>
              <MainVisualMac
                src="pages/downloads/mac.png"
                dpr={2}
                alt="iOS"
                width={600}
                height={419}
              />
            </Container>
          </SectionContainer>
        </SectionContainerWrapper>
      </Page>
    </ServicePageTemplate>
  );
}

const Page = styled(BackgroundContainer)`
  /* background-color: ${background}; */
`;
const SectionContainerWrapper = styled.div`
  background: url(${imagePath('pages/top/services/bg01.png')}) center 8vw
    repeat-y;
  background-size: 100vw auto;
`;
const SectionContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 ${spacing(16)} ${spacing(96)};
  /* padding: ${spacing(128)} ${spacing(16)} ${spacing(96)}; */
  ${mediaMax.tablet} {
    padding: 0 ${spacing(16)} ${spacing(48)} ;
  }
  :last-of-type {
    border-bottom: 0;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: ${spacing(48)} 0;
`;
const Title = styled(Headline)`
  display: inline-block;
  font-size: ${fontSize(44)};
  font-weight: ${fontWeight('bold')};
  text-align: center;
  letter-spacing: -0.04em;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: 6vw;
  }
`;
const Description = styled.div`
  display: block;
  color: ${gray02};
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  text-align: center;
  letter-spacing: 0.04em;

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: ${fontSize(14)};
  }
`;
const Container = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;
const DownloadContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: ${spacing(48)};
  ${mediaMax.smartphone} {
    flex-direction: column;
    align-items: center;
  }
`;
const MainVisualIos = styled(Image)`
  margin-right: ${spacing(32)};
  ${mediaMax.smartphone} {
    width: 170px;
    height: auto;
  }
`;
const MainVisualAndroid = styled(Image)`
  ${mediaMax.smartphone} {
    width: 80px;
    height: auto;
  }
`;
const MainVisualMac = styled(Image)`
  ${mediaMax.smartphone} {
    width: 100%;
    height: auto;
  }
`;
const IOSAppDownloadLink = styled(AppDownloadLink)`
  margin-right: ${spacing(16)};
  ${mediaMax.smartphone} {
    margin-right: 0;
    margin-bottom: ${spacing(16)};
  }
`;

const SectionTitle = styled(Headline)`
  text-align: center;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(36)};
  margin-bottom: ${spacing(16)};
  ${mediaMax.tablet} {
    font-size: 5.2vw;
  }
`;
const SectionText = styled.p`
  text-align: center;
  margin-bottom: ${spacing(32)};
  color: ${gray01};
`;
